import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { CheckCircle2, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import DiffView from '../../components/DiffView';
import { apiErrorMessage } from '../../services/apiService';
import { useAuthToken } from '../../hooks/useAuthToken';
import { useDeployStatus } from './useDeployStatus';
import { isDeployInProgress } from './useReleaseStatus';
import { DeployBadge, shortSha } from './ui';

/** Diffs open by themselves when there are this many files or fewer, so a small change is seen, not just named. */
const AUTO_OPEN = 3;

/** Where to look on the test site after a change: the one file it touched, or the home page. */
const testSiteTarget = (siteUrl, paths) => {
  const base = siteUrl.replace(/\/$/, '');
  if (paths?.length !== 1) return `${base}/`;
  const path = paths[0].replace(/^site\//, '');
  // Clean URLs, as the editor maps them: site/pricing.html is /pricing
  return path === 'index.html' ? `${base}/` : `${base}/${path.replace(/\.html$/, '')}`;
};

const FileRow = ({ file, open, onToggle }) => (
  <div className="mse-bulk-file">
    <button type="button" className="mse-bulk-file-head" onClick={onToggle} aria-expanded={open}>
      {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      <span className="mse-bulk-file-name">{file.url || file.path}</span>
      <span className="mse-bulk-counts">
        <span className="text-success">+{file.linesAdded}</span> <span className="text-danger">-{file.linesRemoved}</span>
      </span>
    </button>
    {open && <DiffView patch={file.patch} maxHeight={260} />}
  </div>
);

/**
 * Review dialog shared by every site-wide change (shared header/footer text, sitemap sync, link
 * fixes...). The feature supplies:
 *   loadPreview(): Promise<{ baseSha, files: [{path, url, patch, linesAdded, linesRemoved}],
 *                            skipped: [{path, reason}], defaultMessage, filesScanned }>
 *   apply({ baseSha, message }): Promise<{ commitSha, commitUrl, paths, deploy }>
 * The server recomputes the change from baseSha on apply, so what is reviewed is what is committed.
 * siteUrl (the test site) adds "Open on test site" once the change is saved; showCount adds the file
 * count to the apply button.
 */
const BulkReviewModal = ({
  show,
  title,
  description,
  applyLabel = 'Apply',
  showCount = true,
  siteUrl,
  loadPreview,
  apply,
  onClose,
  onApplied,
}) => {
  const ensureToken = useAuthToken();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conflict, setConflict] = useState(false);
  const [open, setOpen] = useState(() => new Set());
  const [showSkipped, setShowSkipped] = useState(false);
  const [message, setMessage] = useState('');
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const deploy = useDeployStatus(result?.deploy);

  const runPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    setConflict(false);
    setPreview(null);
    setOpen(new Set());
    try {
      await ensureToken();
      const next = await loadPreview();
      setPreview(next);
      const files = next?.files || [];
      if (files.length <= AUTO_OPEN) setOpen(new Set(files.map((f) => f.path)));
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not prepare this change.'));
    } finally {
      setLoading(false);
    }
  }, [ensureToken, loadPreview]);

  useEffect(() => {
    if (show) {
      setResult(null);
      setMessage('');
      setShowSkipped(false);
      runPreview();
    }
    // Only when the dialog opens.
  }, [show]);

  const doApply = async () => {
    setApplying(true);
    setError(null);
    setConflict(false);
    try {
      await ensureToken();
      const applied = await apply({ baseSha: preview.baseSha, message: message.trim() || undefined });
      setResult(applied);
      onApplied?.(applied);
    } catch (err) {
      setConflict(err?.response?.status === 409);
      setError(apiErrorMessage(err, 'Applying the change failed.'));
    } finally {
      setApplying(false);
    }
  };

  const files = preview?.files || [];
  const skipped = preview?.skipped || [];
  const toggle = (path) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  const allOpen = files.length > 0 && open.size === files.length;
  // The test site shows the old content until its deploy finishes
  const deployRunning = isDeployInProgress(deploy);

  return (
    <Modal show={show} onHide={applying ? undefined : onClose} size="lg" fullscreen="sm-down" backdrop={applying ? 'static' : true}>
      <Modal.Header closeButton={!applying}>
        <Modal.Title>{result ? 'Change applied' : title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {result ? (
          <div>
            <p className="d-flex align-items-center gap-2">
              <CheckCircle2 size={18} className="text-success" />
              Committed {result.paths.length} file{result.paths.length === 1 ? '' : 's'} to the test site as{' '}
              <a href={result.commitUrl} target="_blank" rel="noopener noreferrer">
                <code>{shortSha(result.commitSha)}</code>
              </a>
              .
            </p>
            <div className="d-flex align-items-center gap-2 mb-1">
              Test site deploy: <DeployBadge deploy={deploy} />
              {deployRunning && <Spinner animation="border" size="sm" />}
            </div>
            <p className="mse-hint mb-0">Use Deploy to production in the bar above when the test site looks right.</p>
          </div>
        ) : (
          <>
            {description && <div className="mse-hint mb-3">{description}</div>}
            {loading && (
              <div className="d-flex align-items-center gap-2 text-muted">
                <Spinner animation="border" size="sm" /> Scanning the site
              </div>
            )}
            {error && (
              <Alert variant={conflict ? 'warning' : 'danger'}>
                {error}
                {conflict && (
                  <div className="mt-2">
                    <Button size="sm" variant="outline-secondary" onClick={runPreview}>
                      Preview again
                    </Button>
                  </div>
                )}
              </Alert>
            )}
            {preview && (
              <>
                <div className="mse-bulk-summary">
                  <strong>
                    {files.length === 0 ? 'Nothing to change' : `${files.length} file${files.length === 1 ? '' : 's'} will change`}
                  </strong>
                  <span className="mse-hint">
                    {preview.filesScanned} scanned{skipped.length > 0 ? `, ${skipped.length} skipped` : ''}
                  </span>
                  {files.length > 1 && (
                    <button
                      type="button"
                      className="mse-link-button ms-auto"
                      onClick={() => setOpen(allOpen ? new Set() : new Set(files.map((f) => f.path)))}
                    >
                      {allOpen ? 'Collapse all' : 'Expand all'}
                    </button>
                  )}
                </div>
                <div className="mse-bulk-files">
                  {files.map((file) => (
                    <FileRow key={file.path} file={file} open={open.has(file.path)} onToggle={() => toggle(file.path)} />
                  ))}
                </div>
                {skipped.length > 0 && (
                  <div className="mt-3">
                    <button type="button" className="mse-link-button" onClick={() => setShowSkipped((v) => !v)}>
                      {showSkipped ? 'Hide' : 'Show'} {skipped.length} skipped file{skipped.length === 1 ? '' : 's'}
                    </button>
                    {showSkipped && (
                      <ul className="mse-bulk-skipped">
                        {skipped.map((s) => (
                          <li key={s.path}>
                            <code>{s.path}</code> <span className="mse-hint">{s.reason}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                {files.length > 0 && (
                  <Form.Group className="mt-3">
                    <Form.Label>Commit message</Form.Label>
                    <Form.Control
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={preview.defaultMessage}
                      disabled={applying}
                    />
                  </Form.Group>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {result ? (
          <>
            {siteUrl && (
              <Button
                variant="outline-secondary"
                href={testSiteTarget(siteUrl, result.paths)}
                target="_blank"
                rel="noopener noreferrer"
                disabled={deployRunning}
              >
                <ExternalLink size={14} className="me-1" />
                {deployRunning ? 'Deploying to test site' : 'Open on test site'}
              </Button>
            )}
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline-secondary" onClick={onClose} disabled={applying}>
              Cancel
            </Button>
            <Button variant="primary" onClick={doApply} disabled={applying || loading || files.length === 0}>
              {applying ? 'Applying' : showCount ? `${applyLabel} (${files.length} file${files.length === 1 ? '' : 's'})` : applyLabel}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkReviewModal;
